import { useState } from 'react';
import { Heart, Globe, Users, Check, ArrowRight, ArrowUpRight, Shield, HandHeart, Leaf, ChevronDown, Building, Mail, Gift, Target } from 'lucide-react';
import SEO from '@/components/SEO';
import { site } from '@/data/content';

const whySupport = [
  { icon: HandHeart, title: 'Mission-first support', text: 'Non-profits work with limited budgets and urgent causes. We believe great customer experience tools should not be out of reach.' },
  { icon: Globe, title: 'Global by default', text: 'Whether you operate locally or across borders, ZuriHub helps you respond to donors, volunteers, and beneficiaries from one inbox.' },
  { icon: Leaf, title: 'Built for impact', text: 'Spend less time managing tools and more time delivering programs, raising funds, and serving communities.' },
];

const offering = [
  { icon: Gift, title: '50% lifetime discount', text: 'NGOs receive a permanent 50% discount on Pro and Enterprise agent seats.' },
  { icon: Users, title: 'Unlimited team growth', text: 'Add volunteers, field staff, and coordinators without worrying about per-seat costs scaling too fast.' },
  { icon: Shield, title: 'Trusted compliance', text: 'SOC 2 and GDPR-ready tools so you can manage sensitive beneficiary and donor data responsibly.' },
  { icon: Target, title: 'Priority migration', text: 'Free support to move your existing conversations, contacts, and knowledge base into ZuriHub.' },
];

const eligibility = [
  'Registered non-profit, charity, or social enterprise',
  'Valid registration certificate or equivalent documentation',
  'Annual operating budget under $5M USD, or a field-based NGO under 50 staff',
  'Committed to using ZuriHub for beneficiary, donor, or volunteer support',
];

const faqs = [
  { q: 'How much is the NGO discount?', a: 'Registered NGOs receive a lifetime 50% discount on ZuriHub Pro and Enterprise agent seats. The discount applies to every renewal and new seat.' },
  { q: 'Can NGOs with no formal registration apply?', a: 'We require a registration certificate or equivalent documentation. We review each application individually and may accept local registration or fiscal sponsorship documents.' },
  { q: 'Does the discount apply to add-ons?', a: 'The 50% discount applies to agent seats. Add-ons such as AI Voice numbers, Heatmaps, and Screen Recording are billed at standard rates.' },
  { q: 'How long does approval take?', a: 'We review NGO applications within 2-3 business days and send the next steps by email.' },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'ZuriHub for NGOs',
    description: '50% lifetime discount for registered non-profits, charities, and social enterprises. Omnichannel support, AI, and ticketing for impact-driven teams.',
    url: `${site.baseUrl}/zurihub-for-ngo`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${site.baseUrl}/zurihub-for-ngo` },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

export default function ZuriHubNGO() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    country: '',
    registration: '',
    website: '',
    team: '',
    why: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = `NGO program application from ${form.organization || form.name}`;
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Organization: ${form.organization}`,
      `Country: ${form.country}`,
      `Registration: ${form.registration || 'Not provided'}`,
      `Website: ${form.website || 'Not provided'}`,
      `Team size: ${form.team || 'Not provided'}`,
      '',
      'Why ZuriHub:',
      form.why || 'No details provided',
    ].join('\n');
    window.location.href = `mailto:ngo@getzurihub.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <>
      <SEO
        title="ZuriHub for NGOs"
        description="50% lifetime discount for registered non-profits and social enterprises. Omnichannel support, AI, and ticketing built for impact-driven teams."
        path="/zurihub-for-ngo"
        jsonLd={jsonLd}
      >
        <meta
          name="keywords"
          content="non-profit help desk, NGO customer support, charity support software, non-profit CRM, volunteer management, donor support, social enterprise tools"
        />
      </SEO>

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-28 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
            <Heart className="h-4 w-4" /> For Impact
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            ZuriHub for <span className="gradient-text">NGOs</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
            Registered non-profits, charities, and social enterprises get a lifetime 50% discount on ZuriHub. Spend less on software and more on the communities you serve.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="#apply"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
            >
              Apply now <ChevronDown className="h-4 w-4" />
            </a>
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-7 py-3.5 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Try free <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" aria-labelledby="why-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="why-heading" className="section-title">Support for those who support others</h2>
            <p className="section-subtitle mx-auto mt-3">Purpose-built pricing and features for mission-driven teams.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {whySupport.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-3xl border border-slate-100 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-100 text-brand-700">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="offering-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div>
              <h2 id="offering-heading" className="section-title">What NGOs get</h2>
              <p className="mt-3 text-slate-600">A complete support stack at half the cost, so your budget stays focused on impact.</p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {offering.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="font-bold text-slate-900">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-600">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-xl shadow-slate-200/50">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600" /> Eligibility
              </h3>
              <ul className="mt-5 space-y-3">
                {eligibility.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-2xl bg-slate-900 p-5 text-white">
                <p className="text-sm text-slate-300">
                  <span className="font-bold text-white">Tip:</span> Climate, health, education, and humanitarian NGOs are especially encouraged to apply.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">NGO program FAQs</h2>
          <p className="section-subtitle mx-auto mt-4">Common questions about the discount and approval process.</p>
          <div className="mt-10 space-y-4">
            {faqs.map((faq) => (
              <details key={faq.q} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm group">
                <summary className="flex cursor-pointer list-none items-center justify-between text-left font-semibold text-slate-900">
                  {faq.q}
                  <span className="ml-4 transition group-open:rotate-180">&#9662;</span>
                </summary>
                <p className="mt-3 text-slate-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="py-20" aria-label="Apply for NGO discount">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-10 lg:grid-cols-2">
            <div>
              <h2 className="section-title">Apply for the NGO discount</h2>
              <p className="mt-3 text-slate-600">Tell us about your organization and we will review your application within 2-3 business days.</p>
            </div>

            <div>
              {submitted ? (
                <div className="rounded-3xl border border-green-200 bg-green-50 p-8 text-center shadow-sm">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
                    <Check className="h-7 w-7" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-slate-900">Application ready</h3>
                  <p className="mt-2 text-sm text-slate-600">Your email client is open. Send the message to ngo@getzurihub.com and we will get back to you within 2-3 business days.</p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: '', email: '', organization: '', country: '', registration: '', website: '', team: '', why: '' });
                    }}
                    className="mt-5 rounded-full bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                  >
                    Apply again
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/50">
                  <h3 className="text-xl font-bold text-slate-900">Start your application</h3>
                  <p className="mt-1 text-sm text-slate-600">Fill in your details and we will email you the next steps.</p>
                  <div className="mt-5 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="block text-xs font-semibold text-slate-700">Full name</label>
                        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                          <Users className="h-4 w-4 text-slate-400" />
                          <input id="name" type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400" placeholder="Jane Doe" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="organization" className="block text-xs font-semibold text-slate-700">Organization name</label>
                        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                          <Building className="h-4 w-4 text-slate-400" />
                          <input id="organization" type="text" required value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400" placeholder="Green Future" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-semibold text-slate-700">Work email</label>
                      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400" placeholder="jane@greenfuture.org" />
                      </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="country" className="block text-xs font-semibold text-slate-700">Country</label>
                        <input id="country" type="text" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" placeholder="Uganda" />
                      </div>
                      <div>
                        <label htmlFor="team" className="block text-xs font-semibold text-slate-700">Team size</label>
                        <input id="team" type="text" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" placeholder="5-20" />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="registration" className="block text-xs font-semibold text-slate-700">Registration number or ID</label>
                      <input id="registration" type="text" required value={form.registration} onChange={(e) => setForm({ ...form, registration: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" placeholder="NGO-12345" />
                    </div>
                    <div>
                      <label htmlFor="website" className="block text-xs font-semibold text-slate-700">Website (optional)</label>
                      <input id="website" type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" placeholder="https://greenfuture.org" />
                    </div>
                    <div>
                      <label htmlFor="why" className="block text-xs font-semibold text-slate-700">How will ZuriHub support your mission?</label>
                      <textarea id="why" value={form.why} onChange={(e) => setForm({ ...form, why: e.target.value })} rows={4} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" placeholder="We support donors and field staff across WhatsApp and email..." />
                    </div>
                    <button type="submit" className="w-full rounded-full bg-brand-600 py-3 text-sm font-semibold text-white hover:bg-brand-700">
                      Request NGO discount <ArrowRight className="inline h-4 w-4 ml-2" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
